import qs from 'qs';
import type { IndexUiState } from 'instantsearch.js';
import {
  DEFAULT_SEARCH_SUGGESTIONS,
  SEARCH_SUGGESTIONS,
  SUGGESTION_ATTRIBUTES,
  SearchSuggestion,
  UserBackground,
} from '../settings/searchSuggestions';

export const SEARCH_INDEX = 'documents';

export const getSuggestionsForBackground = (background?: string | null): SearchSuggestion[] =>
  SEARCH_SUGGESTIONS[background?.trim() as UserBackground] ?? [];

// Logged-in users get their course's suggestions. Everyone else, including logged-in users
// whose course has no suggestions (blank, 'School Teacher', 'Other', ...), gets the defaults.
// `isPersonalised` says which, so the UI can label them "Suggested for you" or "Popular searches".
export const getSuggestionsForUser = (
  user?: { userId?: string; userbackground?: string } | null,
): { suggestions: SearchSuggestion[]; isPersonalised: boolean } => {
  const courseSuggestions = user?.userId ? getSuggestionsForBackground(user.userbackground) : [];

  return courseSuggestions.length
    ? { suggestions: courseSuggestions, isPersonalised: true }
    : { suggestions: DEFAULT_SEARCH_SUGGESTIONS, isPersonalised: false };
};

// Keys follow SUGGESTION_ATTRIBUTES rather than the order they were written in the config,
// so the URL matches the one InstantSearch's router writes for the same state.
export const suggestionToIndexUiState = ({ refinements }: SearchSuggestion): IndexUiState => {
  const refinementList: Record<string, string[]> = {};

  SUGGESTION_ATTRIBUTES.forEach((attribute) => {
    if (refinements[attribute]?.length) {
      refinementList[attribute] = refinements[attribute];
    }
  });

  return { refinementList };
};

// Serialises the same way as InstantSearch's default history router (`qs.stringify` of
// the route state), so a pushed URL isn't immediately rewritten as a second history entry.
export const buildSearchPath = (indexUiState: IndexUiState = {}): string => {
  const queryString = qs.stringify({ [SEARCH_INDEX]: indexUiState });

  return queryString ? `/search?${queryString}` : '/search';
};

export const buildSuggestionSearchPath = (suggestion: SearchSuggestion): string =>
  buildSearchPath(suggestionToIndexUiState(suggestion));

const sameValues = (a: string[] = [], b: string[] = []) =>
  a.length === b.length && a.every((value) => b.includes(value));

export const isSuggestionActive = (
  suggestion: SearchSuggestion,
  indexUiState: IndexUiState = {},
): boolean => {
  if (indexUiState.query) {
    return false;
  }

  const expected = suggestionToIndexUiState(suggestion).refinementList ?? {};
  const actual = indexUiState.refinementList ?? {};
  const attributes = new Set([...Object.keys(expected), ...Object.keys(actual)]);

  return Array.from(attributes).every((attribute) =>
    sameValues(expected[attribute], actual[attribute]),
  );
};
